/** Only the variable word changes. CSS reserves space for every configured value. */
export const rotatePromptWords = (root: HTMLElement) => {
    const view = root.ownerDocument.defaultView || window;
    const target = root.querySelector<HTMLElement>('.stgb-ai-conversation__word-value');
    const words = Array.from(root.querySelectorAll<HTMLElement>('.stgb-ai-conversation__word-sizer'))
        .map(element => element.textContent || '').filter(Boolean);
    const motion = view.matchMedia('(prefers-reduced-motion: reduce)');
    if (!target || words.length < 2 || motion.matches) return () => {};

    const segmenter = typeof Intl.Segmenter === 'function'
        ? new Intl.Segmenter(undefined, { granularity: 'grapheme' }) : undefined;
    const letters = words.map(word => segmenter
        ? Array.from(segmenter.segment(word), part => part.segment) : Array.from(word));
    let index = 0;
    let count = 0;
    let deleting = false;
    let timer = 0;
    let active = true;
    root.dataset.rotating = 'true';
    target.textContent = '';

    const tick = () => {
        if (!active) return;
        // Background tabs do not need to keep changing an invisible word.
        if (root.ownerDocument.hidden) { timer = view.setTimeout(tick, 500); return; }
        count += deleting ? -1 : 1;
        target.textContent = letters[index].slice(0, count).join('');
        let delay = deleting ? 45 : 90;
        if (!deleting && count === letters[index].length) {
            deleting = true;
            delay = 2200;
        } else if (deleting && count === 0) {
            deleting = false;
            index = (index + 1) % letters.length;
            delay = 300;
        }
        timer = view.setTimeout(tick, delay);
    };
    const stop = () => {
        active = false;
        view.clearTimeout(timer);
        delete root.dataset.rotating;
        // An editor update may already have committed a different first word.
        target.textContent = root.querySelector<HTMLElement>('.stgb-ai-conversation__word-readable')?.textContent || words[0];
        motion.removeEventListener('change', motionChanged);
    };
    const motionChanged = () => { if (motion.matches) stop(); };
    motion.addEventListener('change', motionChanged);
    timer = view.setTimeout(tick, 450);
    return stop;
};

/** The entrance plays once; the independent word rotation can keep cycling. */
export const playConversation = (root: HTMLElement, onComplete?: () => void) => {
    const view = root.ownerDocument.defaultView || window;
    const clamp = (value: string | undefined, fallback: number, min: number, max: number) => {
        const number = Number(value);
        return value && Number.isFinite(number) ? Math.max(min, Math.min(max, number)) : fallback;
    };
    const thinking = clamp(root.dataset.thinkingDuration, 1.6, 0.6, 4);
    const stagger = clamp(root.dataset.resultStagger, 0.22, 0.1, 0.7);
    const resultCount = root.querySelectorAll('.stgb-ai-conversation__result').length;
    const reducedMotion = view.matchMedia('(prefers-reduced-motion: reduce)');
    if (reducedMotion.matches) {
        root.dataset.motion = 'complete';
        onComplete?.();
        return () => {};
    }

    const panelDelay = 0.7;
    const thinkingDelay = panelDelay + 0.7;
    const answerDelay = thinkingDelay + thinking;
    root.style.setProperty('--stgb-ai-panel-delay', panelDelay + 's');
    root.style.setProperty('--stgb-ai-thinking-delay', thinkingDelay + 's');
    root.style.setProperty('--stgb-ai-answer-delay', answerDelay + 's');
    root.style.setProperty('--stgb-ai-stagger', stagger + 's');
    root.dataset.motion = 'waiting';
    void root.offsetWidth;
    root.dataset.motion = 'playing';

    let active = true;
    let timer = 0;
    const finish = (notify = true) => {
        if (!active) return;
        active = false;
        view.clearTimeout(timer);
        reducedMotion.removeEventListener('change', motionChanged);
        root.dataset.motion = 'complete';
        if (notify) onComplete?.();
    };
    const motionChanged = () => { if (reducedMotion.matches) finish(); };
    reducedMotion.addEventListener('change', motionChanged);
    const duration = (answerDelay + 0.45 + Math.max(0, resultCount - 1) * stagger + 0.65) * 1000;
    timer = view.setTimeout(() => finish(), duration);
    return () => finish(false);
};
